import type { ChainCallRecord } from "@passio/easyai-client";

export type ChainCodeLang = "curl" | "ts" | "fetch" | "python";

export const CHAIN_CODE_LANGS: { value: ChainCodeLang; label: string }[] = [
  { value: "curl", label: "cURL" },
  { value: "ts", label: "TypeScript client" },
  { value: "fetch", label: "fetch" },
  { value: "python", label: "Python" },
];

const shq = (s: string) => `'${s.replace(/'/g, `'\\''`)}'`;

function truncateDataUris(v: unknown): unknown {
  if (typeof v === "string") return v.startsWith("data:") && v.length > 120 ? `${v.slice(0, 60)}…(${v.length} chars)` : v;
  if (Array.isArray(v)) return v.map(truncateDataUris);
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, truncateDataUris(x)]));
  return v;
}

const indentTail = (s: string, pad: string) => s.replace(/\n/g, `\n${pad}`);

/** Python literal for JSON data (`True`/`False`/`None`; JSON string escapes are valid Python). */
export function pyLiteral(v: unknown, indent = ""): string {
  if (v === null || v === undefined) return "None";
  if (typeof v === "boolean") return v ? "True" : "False";
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "None";
  if (typeof v === "string") return JSON.stringify(v);
  const inner = `${indent}    `;
  if (Array.isArray(v)) return v.length ? `[\n${v.map((x) => `${inner}${pyLiteral(x, inner)},`).join("\n")}\n${indent}]` : "[]";
  const entries = Object.entries(v as Record<string, unknown>);
  return entries.length ? `{\n${entries.map(([k, x]) => `${inner}${JSON.stringify(k)}: ${pyLiteral(x, inner)},`).join("\n")}\n${indent}}` : "{}";
}

interface Parts {
  base: string;
  chainId: string;
  executionId: string | undefined;
  input: Record<string, unknown>;
  sink: string | undefined;
  label: string;
}

function parts(r: ChainCallRecord, origin: string): Parts {
  return {
    base: origin.replace(/\/+$/, ""),
    chainId: r.chainId,
    executionId: r.executionId,
    input: truncateDataUris(r.requestBody.input_data) as Record<string, unknown>,
    sink: r.sinkNodeId,
    label: r.label ?? r.chainKey ?? "chain",
  };
}

function curl(p: Parts): string {
  const sink = p.sink ? `node_results["${p.sink}"].output` : "the last entry in node_results (its output)";
  return [
    `# 1. Start the run. It returns at once with an execution id.`,
    [
      `curl -X POST ${shq(`${p.base}/api/v2/chains/${p.chainId}/execute`)}`,
      `  -H "Authorization: Bearer $EASYAI_API_KEY"`,
      `  -H 'Content-Type: application/json'`,
      `  -d ${shq(JSON.stringify({ input_data: p.input }))}`,
    ].join(" \\\n"),
    ``,
    `# 2. Poll every 1-2 s until status is completed, failed or cancelled.`,
    [
      `curl ${p.executionId ? shq(`${p.base}/api/v2/chains/executions/${p.executionId}`) : `"${p.base}/api/v2/chains/executions/$EXECUTION_ID"`}`,
      `  -H "Authorization: Bearer $EASYAI_API_KEY"`,
    ].join(" \\\n"),
    ``,
    `# 3. The result is ${sink} (a JSON string or object).`,
    `#    Tokens used: total_tokens, or the sum of each step's tokenUsage.`,
  ].join("\n");
}

function typescript(p: Parts): string {
  return `import { EasyAIClient, executionTokens } from "@passio/easyai-client";

// Server-side only: the EasyAI key must never ship in a browser or app bundle.
const easyai = new EasyAIClient({ baseUrl: "${p.base}", apiKey: process.env.EASYAI_API_KEY! });

// ${p.label}: execute, poll every 1.5 s, then parse the ${p.sink ? `${p.sink} output` : "last step's output"}.
const { execution, output } = await easyai.run(
  "${p.chainId}",
  ${indentTail(JSON.stringify(p.input, null, 2), "  ")},
  {${p.sink ? ` sinkNodeId: "${p.sink}",` : ""} onProgress: (p) => console.log(p.status, \`\${p.polls} polls\`) },
);
console.log(execution.id, executionTokens(execution), output);`;
}

function fetchCode(p: Parts): string {
  const pick = p.sink ? `execution.node_results[${JSON.stringify(p.sink)}]` : "Object.values(execution.node_results).at(-1)";
  return `// Server-side only: the EasyAI key must never ship in a browser or app bundle.
const BASE = "${p.base}";
const headers = { Authorization: \`Bearer \${process.env.EASYAI_API_KEY}\`, "Content-Type": "application/json" };

// 1. Start the run (${p.label}). It returns at once with an execution id.
const startRes = await fetch(\`\${BASE}/api/v2/chains/${p.chainId}/execute\`, {
  method: "POST",
  headers,
  body: JSON.stringify({
    input_data: ${indentTail(JSON.stringify(p.input, null, 2), "    ")},
  }),
});
if (!startRes.ok) throw new Error(\`execute failed: \${startRes.status} \${await startRes.text()}\`);
const started = await startRes.json();

// 2. Poll until the execution finishes.
let execution;
for (let i = 0; i < 240; i++) {
  await new Promise((r) => setTimeout(r, 1500));
  execution = await fetch(\`\${BASE}/api/v2/chains/executions/\${started.id}\`, { headers }).then((r) => r.json());
  if (["completed", "failed", "cancelled"].includes(execution.status)) break;
}
if (execution?.status !== "completed") throw new Error(execution?.error ?? \`chain \${execution?.status ?? "timed out"}\`);

// 3. Read the result step. Outputs can be a JSON string or an object.
const raw = ${pick}?.output;
let output = raw;
if (typeof raw === "string") {
  try {
    output = JSON.parse(raw);
  } catch {
    // plain-text output
  }
}
console.log(execution.id, execution.total_tokens, output);`;
}

function python(p: Parts): string {
  const pick = p.sink ? `execution["node_results"][${JSON.stringify(p.sink)}]` : `list(execution["node_results"].values())[-1]`;
  return `import json
import os
import time

import requests

# Server-side only: the EasyAI key must never ship in a browser or app bundle.
BASE = "${p.base}"
HEADERS = {"Authorization": f"Bearer {os.environ['EASYAI_API_KEY']}"}

# 1. Start the run (${p.label}). It returns at once with an execution id.
input_data = ${pyLiteral(p.input)}
res = requests.post(
    f"{BASE}/api/v2/chains/${p.chainId}/execute",
    headers=HEADERS,
    json={"input_data": input_data},
    timeout=30,
)
res.raise_for_status()
execution_id = res.json()["id"]

# 2. Poll until the execution finishes.
execution = {}
for _ in range(240):
    time.sleep(1.5)
    execution = requests.get(f"{BASE}/api/v2/chains/executions/{execution_id}", headers=HEADERS, timeout=30).json()
    if execution["status"] in ("completed", "failed", "cancelled"):
        break
if execution.get("status") != "completed":
    raise RuntimeError(execution.get("error") or f"chain {execution.get('status', 'timed out')}")

# 3. Read the result step. Outputs can be a JSON string or an object.
raw = ${pick}.get("output")
output = raw
if isinstance(raw, str):
    try:
        output = json.loads(raw)
    except ValueError:
        pass  # plain-text output
print(execution_id, execution.get("total_tokens"), output)`;
}

/** Code that makes the same chain run directly against EasyAI. The key is always read from `EASYAI_API_KEY`. */
export function chainCode(lang: ChainCodeLang, r: ChainCallRecord, origin: string): string {
  const p = parts(r, origin);
  if (lang === "curl") return curl(p);
  if (lang === "ts") return typescript(p);
  if (lang === "fetch") return fetchCode(p);
  return python(p);
}
